import { useEffect, useState, useRef } from "react";
import { Store, BadgeCheck, Copy, ExternalLink, Upload, Save, Link2, Palette, Truck, RotateCcw, Search as SearchIcon, Eye, QrCode } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import StoreQRPoster from "@/components/StoreQRPoster";

const readB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
const ACCENTS = ["#4f46e5", "#2563eb", "#0891b2", "#059669", "#d97706", "#db2777", "#7c3aed", "#0f172a"];
const SECTIONS = [["categories", "Categories"], ["offers", "Offers"], ["featured", "Featured products"], ["about", "About"], ["shipping", "Shipping"], ["returns", "Returns"], ["policies", "Seller policies"], ["contact", "Contact"]];
const BLANK = { name: "", about: "", location: "", policies: "", tagline: "", support_email: "", support_phone: "", hours: "", shipping_info: "", processing_time: "", return_info: "", return_window_days: "", seo_title: "", seo_description: "", announcement: "" };

export default function SellerStorefront() {
  const [sf, setSf] = useState(null);
  const [verified, setVerified] = useState(false);
  const [slug, setSlug] = useState("");
  const [form, setForm] = useState(BLANK);
  const [accent, setAccent] = useState("#4f46e5");
  const [status, setStatus] = useState("active");
  const [sections, setSections] = useState({});
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);
  const logoRef = useRef(null); const bannerRef = useRef(null);

  const load = () => {
    api.get("/marketplace/storefront/me").then((r) => {
      const s = r.data.storefront; setSf(s);
      if (s) {
        setSlug(s.slug || "");
        setForm({ ...BLANK, ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, s[k] ?? ""])) });
        setAccent(s.accent || "#4f46e5"); setStatus(s.status || "active");
        setSections(s.sections || {}); setFeatured(s.featured_product_ids || []);
      }
    }).catch(() => {});
    api.get("/marketplace/seller/selling-area").then((r) => setVerified(!!r.data.approved)).catch(() => {});
    api.get("/marketplace/seller/products").then((r) => setProducts((r.data.products || []).filter((p) => p.status === "published"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const claim = async () => {
    if (!slug.trim()) { toast.error("Enter a store URL"); return; }
    try { const r = await api.post("/marketplace/storefront/claim", { slug, name: form.name || undefined }); setSf(r.data.storefront); toast.success("Store URL saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    const payload = { ...form, accent, status, sections, featured_product_ids: featured };
    payload.return_window_days = form.return_window_days === "" ? null : Number(form.return_window_days);
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    try { const r = await api.put("/marketplace/storefront/profile", payload); setSf(r.data.storefront); toast.success("Storefront saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadImg = async (kind, file) => {
    if (!file) return;
    try { const b64 = await readB64(file); await api.post("/marketplace/storefront/image", { kind, filename: file.name, content_b64: b64 }); toast.success(`${kind} updated`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSection = (k) => setSections((s) => ({ ...s, [k]: s[k] === false ? true : false }));
  const toggleFeatured = (id) => setFeatured((f) => f.includes(id) ? f.filter((x) => x !== id) : (f.length < 20 ? [...f, id] : f));
  const storeUrl = sf?.slug ? `${window.location.origin}/${sf.slug}` : "";
  const copy = () => { navigator.clipboard.writeText(storeUrl); toast.success("Store link copied"); };
  const inp = (k, ph, extra = {}) => <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} data-testid={`store-${k}`} {...extra} />;

  return (
    <div data-testid="seller-storefront-page">
      <PageHeader title="Store Settings" icon={Store} subtitle="Your public Suffle marketplace store. Customers can visit it at your store URL." />

      {!verified && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="store-not-verified">
          Your seller account isn't approved yet — your store can't go Active publicly until you're verified. You can still set it up now.
        </div>
      )}

      <SectionCard title="Store URL" icon={Link2} testid="store-url">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-400">{window.location.host}/</span>
          <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="your-store" className="w-56" data-testid="store-slug-input" />
          <button onClick={claim} data-testid="store-slug-save" className="rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Save URL</button>
          {sf?.slug && (<>
            <button onClick={copy} data-testid="store-copy-link" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300"><Copy className="h-3 w-3" /> Copy link</button>
            <a href={storeUrl} target="_blank" rel="noreferrer noopener noreferrer" data-testid="store-preview" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300"><Eye className="h-3 w-3" /> Preview store</a>
          </>)}
        </div>
        <p className="mt-2 text-xs text-slate-400">3–40 lowercase letters, numbers or hyphens. Reserved names (admin, shop, cart…) aren't allowed.</p>
      </SectionCard>

      <SectionCard title="Promote — Store QR poster" icon={QrCode} testid="store-qr">
        {sf?.slug ? (
          <>
            <p className="mb-3 text-xs text-slate-500">A ready-to-share poster with a QR code that opens your store on Suffle. Download it as a JPG, or share the link anywhere.</p>
            <StoreQRPoster storeName={form.name || sf.name || "Your Store"} storeUrl={storeUrl} slug={sf.slug} logo={sf.logo} accent={accent} />
          </>
        ) : (
          <p className="text-sm text-slate-500" data-testid="store-qr-no-slug">Set your <b>Store URL</b> above first — then your personalized QR poster will appear here to download and share.</p>
        )}
      </SectionCard>

      {sf && (<>
        {/* Live preview strip */}
        <div className="mb-5 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="store-preview-card">
          <div className="relative h-40 w-full overflow-hidden sm:h-56" data-testid="store-preview-banner" style={sf.banner ? undefined : { backgroundColor: "#0b0a1f", backgroundImage: `radial-gradient(70% 60% at 15% 0%, ${accent}99, transparent 60%)` }}>
            {sf.banner && (<>
              <div className="absolute inset-0 scale-110 blur-2xl" style={{ backgroundImage: `url(${sf.banner})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              <div className="absolute inset-0" style={{ backgroundImage: `url(${sf.banner})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" }} />
            </>)}
          </div>
          <div className="flex items-end gap-3 px-4 pb-4">
            <div className="-mt-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow ring-1 ring-slate-200">
              {sf.logo ? <img src={sf.logo} alt="" className="h-full w-full rounded-2xl object-cover" /> : <Store className="h-6 w-6 text-slate-400" />}
            </div>
            <div className="pb-1">
              <div className="flex items-center gap-2"><p className="font-bold text-slate-800">{form.name || sf.name || "Your store"}</p>
                {verified && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><BadgeCheck className="h-3 w-3" /> Verified Seller</span>}
                <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ backgroundColor: status === "active" ? "#059669" : "#d97706" }}>{status === "active" ? "Active" : "Temporarily unavailable"}</span></div>
              {form.tagline && <p className="text-xs text-slate-500">{form.tagline}</p>}
              <p className="text-xs text-slate-400">{storeUrl}</p>
            </div>
          </div>
        </div>

        {/* Branding */}
        <SectionCard title="Branding" icon={Palette} testid="store-branding">
          <div className="flex flex-wrap gap-3">
            <input ref={logoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadImg("logo", e.target.files?.[0])} data-testid="store-logo-input" />
            <input ref={bannerRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => uploadImg("banner", e.target.files?.[0])} data-testid="store-banner-input" />
            <button onClick={() => logoRef.current?.click()} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300"><Upload className="h-3 w-3" /> Upload logo</button>
            <button onClick={() => bannerRef.current?.click()} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300"><Upload className="h-3 w-3" /> Upload banner</button>
          </div>
          <p className="mt-2 mb-1 text-xs text-slate-500">Recommended: <b>Logo</b> 512×512px (square) · <b>Banner</b> wide landscape ~1600×400px. Your full banner is always shown (never cropped) — any leftover space is filled with a softly blurred version of your banner, so a matching-ratio image fills best.</p>
          <p className="mb-3 text-xs text-slate-400">JPG/PNG/WEBP up to 4 MB. Images are security-scanned before publishing.</p>
          <p className="mb-1 text-xs font-medium text-slate-500">Accent colour</p>
          <div className="flex flex-wrap gap-2" data-testid="store-accents">
            {ACCENTS.map((c) => <button key={c} onClick={() => setAccent(c)} data-testid={`accent-${c.slice(1)}`} className={`h-7 w-7 rounded-full ring-2 ${accent === c ? "ring-slate-800" : "ring-transparent"}`} style={{ backgroundColor: c }} />)}
          </div>
        </SectionCard>

        {/* Basic info */}
        <SectionCard title="Basic information" icon={Store} testid="store-basic">
          <div className="grid gap-3">
            {inp("name", "Store name")}
            {inp("tagline", "Brand tagline (e.g. Handmade in Assam)")}
            <textarea value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="About your store / brand story" rows={3} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="store-about" />
            {inp("location", "Business location (e.g. Nalbari, Assam)")}
            {inp("announcement", "Store announcement banner (optional)")}
          </div>
        </SectionCard>

        {/* Contact */}
        <SectionCard title="Contact & support" icon={Store} testid="store-contact-settings">
          <div className="grid gap-3 sm:grid-cols-2">
            {inp("support_email", "Support email", { type: "email" })}
            {inp("support_phone", "Support phone")}
            <div className="sm:col-span-2">{inp("hours", "Business hours (e.g. Mon–Sat, 10am–7pm)")}</div>
          </div>
        </SectionCard>

        {/* Shipping */}
        <SectionCard title="Shipping & delivery" icon={Truck} testid="store-shipping-settings">
          <div className="grid gap-3">
            {inp("processing_time", "Processing/dispatch time (e.g. Ships in 1–2 days)")}
            <textarea value={form.shipping_info} onChange={(e) => setForm({ ...form, shipping_info: e.target.value })} placeholder="Shipping information / methods / charges where configured" rows={3} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="store-shipping_info" />
          </div>
          <p className="mt-2 text-xs text-slate-400">Serviceable areas come from your Selling Areas automatically. You are responsible for courier dispatch — Suffle doesn't operate your courier.</p>
        </SectionCard>

        {/* Returns */}
        <SectionCard title="Returns & replacements" icon={RotateCcw} testid="store-returns-settings">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input type="number" value={form.return_window_days} onChange={(e) => setForm({ ...form, return_window_days: e.target.value })} placeholder="Return window (days)" data-testid="store-return_window_days" />
            <div className="sm:col-span-3"><textarea value={form.return_info} onChange={(e) => setForm({ ...form, return_info: e.target.value })} placeholder="Return / replacement conditions" rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="store-return_info" /></div>
          </div>
          <p className="mt-2 text-xs text-slate-400">Your return info is subordinate to Suffle's central Return, Replacement &amp; Refund Policy and cannot override mandatory marketplace rules.</p>
        </SectionCard>

        {/* Seller policies */}
        <SectionCard title="Seller policies" icon={Store} testid="store-policies-settings">
          <textarea value={form.policies} onChange={(e) => setForm({ ...form, policies: e.target.value })} placeholder="Seller-specific policies summary" rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="store-policies" />
        </SectionCard>

        {/* Sections visibility */}
        <SectionCard title="Storefront sections" icon={Store} testid="store-sections">
          <p className="mb-2 text-xs text-slate-400">Show or hide sections on your public store.</p>
          <div className="flex flex-wrap gap-2">
            {SECTIONS.map(([k, lbl]) => (
              <button key={k} onClick={() => toggleSection(k)} data-testid={`section-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${sections[k] === false ? "bg-slate-100 text-slate-400 line-through" : "text-white"}`} style={sections[k] === false ? {} : { backgroundColor: accent }}>{lbl}</button>
            ))}
          </div>
        </SectionCard>

        {/* Featured products */}
        <SectionCard title="Featured products" icon={Store} testid="store-featured-settings">
          {products.length === 0 ? <p className="text-xs text-slate-400">No published products yet. Publish products to feature them.</p> : (
            <div className="grid gap-2 sm:grid-cols-2">
              {products.map((p) => (
                <label key={p.product_id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${featured.includes(p.product_id) ? "border-blue-400 bg-blue-50" : "border-slate-200"}`} data-testid={`feature-${p.product_id}`}>
                  <input type="checkbox" checked={featured.includes(p.product_id)} onChange={() => toggleFeatured(p.product_id)} />
                  <span className="truncate">{p.name}</span>
                </label>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-slate-400">Only approved + published products can be featured (max 20).</p>
        </SectionCard>

        {/* SEO */}
        <SectionCard title="SEO" icon={SearchIcon} testid="store-seo">
          <div className="grid gap-3">
            {inp("seo_title", "SEO title (max 70 chars)")}
            <textarea value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="Meta description (max 200 chars)" rows={2} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="store-seo_description" />
          </div>
        </SectionCard>

        {/* Status + Save */}
        <SectionCard title="Store status" icon={Store} testid="store-status">
          <div className="flex flex-wrap items-center gap-3">
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="store-status-select" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="active">Active (public &amp; taking orders)</option>
              <option value="unavailable">Temporarily unavailable</option>
            </select>
            {status === "active" && !verified && <span className="text-xs text-amber-600">Requires a verified seller account to go live.</span>}
          </div>
        </SectionCard>

        <button onClick={save} data-testid="store-save-profile" className="mb-8 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-6 py-3 text-sm font-semibold text-white"><Save className="h-4 w-4" /> Save storefront</button>
      </>)}
    </div>
  );
}
